import React from 'react';
import { Link } from 'react-router-dom';
import '../../styles/ServiceDetail.css';

function CloudDevOps() {
  return (
    <div className="service-detail">
      <section className="service-hero">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/services">Services</Link> / Cloud & DevOps Setup
          </div>
          <h1>Cloud & DevOps Setup for Startups</h1>
          <p className="hero-subtitle">
            Build scalable, secure, and cost-effective cloud infrastructure from day one
          </p>
        </div>
      </section>

      <section className="service-overview">
        <div className="container">
          <div className="overview-content">
            <h2>Production-Ready Cloud Infrastructure</h2>
            <p>
              We help startups and growing SMEs establish robust cloud infrastructure on AWS or Azure. 
              Our DevOps solutions ensure your applications are deployed reliably, scale effortlessly, 
              and operate cost-efficiently. From initial setup to ongoing optimization, we provide 
              enterprise-grade infrastructure accessible to businesses of all sizes.
            </p>
          </div>

          <div className="key-benefits">
            <h3>Key Benefits</h3>
            <div className="benefits-list">
              <div className="benefit">
                <span className="benefit-icon">🚀</span>
                <div>
                  <h4>Faster Time to Market</h4>
                  <p>Deploy applications in days, not months</p>
                </div>
              </div>
              <div className="benefit">
                <span className="benefit-icon">💰</span>
                <div>
                  <h4>30-50% Cost Savings</h4>
                  <p>Optimize cloud spending with right-sized resources</p>
                </div>
              </div>
              <div className="benefit">
                <span className="benefit-icon">🔒</span>
                <div>
                  <h4>Enhanced Security</h4>
                  <p>Implement industry best practices from the start</p>
                </div>
              </div>
              <div className="benefit">
                <span className="benefit-icon">📊</span>
                <div>
                  <h4>99.9% Uptime</h4>
                  <p>Reliable infrastructure with automated failover</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="what-we-offer">
        <div className="container">
          <h2>Our Cloud & DevOps Services</h2>
          
          <div className="automation-areas">
            <div className="area-card">
              <h3>☁️ Cloud Infrastructure Setup</h3>
              <ul>
                <li>AWS/Azure account setup and organization</li>
                <li>Multi-environment configuration (dev, staging, prod)</li>
                <li>Virtual Private Cloud (VPC) design</li>
                <li>Network architecture and security groups</li>
                <li>Load balancing and auto-scaling setup</li>
                <li>Database provisioning (RDS, DynamoDB, CosmosDB)</li>
              </ul>
            </div>

            <div className="area-card">
              <h3>🔄 CI/CD Pipeline Implementation</h3>
              <ul>
                <li>GitHub Actions / GitLab CI setup</li>
                <li>Jenkins pipeline configuration</li>
                <li>Automated testing integration</li>
                <li>Container orchestration (Docker, Kubernetes)</li>
                <li>Blue-green and canary deployments</li>
                <li>Rollback strategies and versioning</li>
              </ul>
            </div>

            <div className="area-card">
              <h3>🏗️ Infrastructure as Code</h3>
              <ul>
                <li>Terraform modules and configurations</li>
                <li>AWS CloudFormation templates</li>
                <li>Azure Resource Manager (ARM)</li>
                <li>Version-controlled infrastructure</li>
                <li>Reusable infrastructure patterns</li>
                <li>Environment replication</li>
              </ul>
            </div>

            <div className="area-card">
              <h3>💸 Cost Optimization</h3>
              <ul>
                <li>Cloud spend analysis and reporting</li>
                <li>Resource right-sizing recommendations</li>
                <li>Reserved instances planning</li>
                <li>Spot instance utilization</li>
                <li>Storage optimization strategies</li>
                <li>Budget alerts and controls</li>
              </ul>
            </div>

            <div className="area-card">
              <h3>🔐 Security & Compliance</h3>
              <ul>
                <li>IAM roles and policies setup</li>
                <li>Encryption at rest and in transit</li>
                <li>Secrets management (AWS Secrets Manager, Vault)</li>
                <li>Security group configuration</li>
                <li>Compliance framework implementation</li>
                <li>Vulnerability scanning and patching</li>
              </ul>
            </div>

            <div className="area-card">
              <h3>📈 Monitoring & Alerting</h3>
              <ul>
                <li>CloudWatch / Azure Monitor setup</li>
                <li>Application performance monitoring</li>
                <li>Log aggregation (ELK, Splunk)</li>
                <li>Custom metrics and dashboards</li>
                <li>Automated alerting and incident response</li>
                <li>Uptime monitoring and SLA tracking</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="technologies">
        <div className="container">
          <h2>Technologies & Platforms</h2>
          <div className="tech-list">
            <div className="tech-item">AWS</div>
            <div className="tech-item">Azure</div>
            <div className="tech-item">Terraform</div>
            <div className="tech-item">Docker</div>
            <div className="tech-item">Kubernetes</div>
            <div className="tech-item">GitHub Actions</div>
            <div className="tech-item">Jenkins</div>
            <div className="tech-item">GitLab CI</div>
            <div className="tech-item">CloudFormation</div>
            <div className="tech-item">Ansible</div>
          </div>
        </div>
      </section>

      <section className="implementation-process">
        <div className="container">
          <h2>Our Implementation Approach</h2>
          <div className="process-timeline">
            <div className="timeline-item">
              <div className="timeline-marker">1</div>
              <div className="timeline-content">
                <h4>Requirements Assessment</h4>
                <p>Understand your application needs, traffic patterns, and growth projections</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">2</div>
              <div className="timeline-content">
                <h4>Architecture Design</h4>
                <p>Create scalable, secure infrastructure blueprint aligned with best practices</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">3</div>
              <div className="timeline-content">
                <h4>Infrastructure Provisioning</h4>
                <p>Build environments using Infrastructure as Code for repeatability</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">4</div>
              <div className="timeline-content">
                <h4>Pipeline Setup</h4>
                <p>Implement CI/CD workflows for automated testing and deployment</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">5</div>
              <div className="timeline-content">
                <h4>Monitoring & Optimization</h4>
                <p>Set up observability and continuously optimize for cost and performance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-models">
        <div className="container">
          <h2>Flexible Engagement Models</h2>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3>Quick Start Package</h3>
              <p>Perfect for early-stage startups</p>
              <ul>
                <li>Single environment setup</li>
                <li>Basic CI/CD pipeline</li>
                <li>Essential monitoring</li>
                <li>2-week delivery</li>
              </ul>
            </div>
            <div className="pricing-card">
              <h3>Complete Setup</h3>
              <p>Ideal for growing companies</p>
              <ul>
                <li>Multi-environment infrastructure</li>
                <li>Advanced CI/CD with testing</li>
                <li>Full monitoring & alerting</li>
                <li>4-6 week delivery</li>
              </ul>
            </div>
            <div className="pricing-card">
              <h3>Managed Services</h3>
              <p>Ongoing support & optimization</p>
              <ul>
                <li>24/7 monitoring</li>
                <li>Regular cost optimization</li>
                <li>Security updates</li>
                <li>Monthly retainer</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Ready to Build Your Cloud Infrastructure?</h2>
          <p>Let's design a scalable, cost-effective solution for your business</p>
          <Link to="/contact" className="btn btn-primary">Get Started Today</Link>
        </div>
      </section>
    </div>
  );
}

export default CloudDevOps;
